import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaperPlane, 
  faSpinner,
  faExclamationTriangle,
  faTimes,
  faPlug,
  faCopy,
  faGripVertical
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import ExamFlipLogo from '../../assets/examflip-transparent-logo.png';
import './QuestionAIChat.override.css';

const QuestionAIChat = forwardRef(({ questionId, examId, isOpen, onClose, isDarkMode, themeStyles }, ref) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [websocket, setWebsocket] = useState(null);
  // Mobile detection - initialize with correct value immediately
  const [isMobile, setIsMobile] = useState(() => {
    const mobile = window.innerWidth <= 768;
    // console.log('[QuestionAIChat] Initial mobile detection:', { 
    //   width: window.innerWidth, 
    //   isMobile: mobile 
    // });
    return mobile;
  });
  const [error, setError] = useState('');
  const [reconnectStatus, setReconnectStatus] = useState('');
  const [isAiTyping, setIsAiTyping] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [reconnectTimer, setReconnectTimer] = useState(null);
  const [reconnectMessageId, setReconnectMessageId] = useState(null);
  const [isManuallyClosed, setIsManuallyClosed] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  
  // Drag functionality state
  const [isDragging, setIsDragging] = useState(false);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  const autoCloseTimerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const modalRef = useRef(null);
  const { user } = useAuth();
  const websocketRef = useRef(null);
  const prevQuestionIdRef = useRef(null);

  const getUserInitials = () => {
    if (!user || !user.firstName || !user.lastName) return '';
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Mobile detection effect
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      // console.log('[QuestionAIChat] Resize mobile detection:', { 
      //   width: window.innerWidth, 
      //   isMobile: mobile,
      //   changed: mobile !== isMobile
      // });
      setIsMobile(mobile);
    };
    
    // Only listen for resize events - initial value is already set correctly
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobile]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isAiTyping]);

  // Main connection management effect - handles all connection scenarios
  useEffect(() => {
    // console.log(`[QuestionAIChat] Connection effect triggered:`, {
    //   isOpen,
    //   isMinimized,
    //   questionId,
    //   examId,
    //   isConnected,
    //   isConnecting,
    //   isManuallyClosed,
    //   hasWebSocket: !!websocketRef.current,
    //   currentWebSocketReadyState: websocketRef.current ? websocketRef.current.readyState : 'none'
    // });

    // Check if this is a question change
    if (prevQuestionIdRef.current && prevQuestionIdRef.current !== questionId) {
      // console.log(`[QuestionAIChat] 🔄 QUESTION CHANGED from ${prevQuestionIdRef.current} to ${questionId} - CLOSING CHAT`);
      
      // Question changed - close the chat completely
      if (websocketRef.current) {
        // console.log(`[QuestionAIChat] 🚪 Closing chat due to question change`);
        try {
          websocketRef.current.close(1000, 'Question changed');
        } catch (error) {
          console.warn(`[QuestionAIChat] Error closing WebSocket:`, error);
        }
        websocketRef.current = null;
      }
      
      // Clear all state INCLUDING minimized state
      setIsConnected(false);
      setIsConnecting(false);
      setWebsocket(null);
      setMessages([]);
      setError('');
      setReconnectStatus('');
      setReconnectAttempts(0);
      setReconnectMessageId(null);
      setIsAiTyping(false);
      setIsManuallyClosed(false);
      setIsMinimized(false); // ✅ Reset minimized state on question change
      
      // console.log(`[QuestionAIChat] 🔄 Reset minimized state to false for new question`);
      
      // Clear timers
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        setReconnectTimer(null);
      }
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
      
      // Close the chat UI completely
      // console.log(`[QuestionAIChat] 📱 Closing chat UI due to question change`);
      onClose();
      
      // Update the ref for next comparison
      prevQuestionIdRef.current = questionId;
      return; // Exit early - don't connect to new question automatically
    }
    
    // Update the ref for next comparison
    prevQuestionIdRef.current = questionId;

    // Reset manually closed flag when chat is opened (user wants to connect)
    if (isOpen && isManuallyClosed) {
      // console.log(`[QuestionAIChat] 🔄 Chat opened - resetting manually closed flag`);
      setIsManuallyClosed(false);
    }

    // Only connect if chat is explicitly opened (not on question change) and not manually closed
    if (isOpen && questionId && examId && !isManuallyClosed) {
      // Check if we need to cleanup existing connection first
      if (websocketRef.current) {
        const currentWs = websocketRef.current;
        const isCurrentlyConnected = currentWs.readyState === WebSocket.OPEN || currentWs.readyState === WebSocket.CONNECTING;
        
        if (isCurrentlyConnected) {
          // console.log(`[QuestionAIChat] 🧹 Cleaning up existing WebSocket connection before new connection`);
          try {
            currentWs.close(1000, 'New connection requested');
          } catch (error) {
            console.warn(`[QuestionAIChat] Error closing existing WebSocket:`, error);
          }
          
          // Clear all connection state
          setIsConnected(false);
          setIsConnecting(false);
          setWebsocket(null);
          setMessages([]);
          setError('');
          setReconnectStatus('');
          setReconnectAttempts(0);
          setReconnectMessageId(null);
          setIsAiTyping(false);
          
          // Clear timers
          if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            setReconnectTimer(null);
          }
          if (autoCloseTimerRef.current) {
            clearTimeout(autoCloseTimerRef.current);
            autoCloseTimerRef.current = null;
          }
        }
        
        websocketRef.current = null;
      }

      // console.log(`[QuestionAIChat] ✅ Connecting to question ${questionId} (chat explicitly opened, isMinimized: ${isMinimized})`);
      // Small delay to ensure cleanup is complete
      const connectTimeout = setTimeout(() => {
        connectToChat();
      }, 100);
      
      return () => {
        clearTimeout(connectTimeout);
      };
    } else {
      // console.log(`[QuestionAIChat] ❌ Not connecting:`, {
      //   isOpen,
      //   hasQuestionId: !!questionId,
      //   hasExamId: !!examId,
      //   isManuallyClosed,
      //   isMinimized,
      //   reason: !isOpen ? 'Chat not open' : 
      //           !questionId ? 'No question ID' : 
      //           !examId ? 'No exam ID' : 
      //           isManuallyClosed ? 'Manually closed' : 'Unknown'
      // });
    }

    // Cleanup function
    return () => {
      // console.log(`[QuestionAIChat] 🧽 useEffect cleanup function called`);
      if (websocketRef.current && websocketRef.current.readyState !== WebSocket.CLOSED) {
        // console.log(`[QuestionAIChat] Cleanup: closing WebSocket connection`);
        websocketRef.current.close(1000, 'Component cleanup');
        websocketRef.current = null;
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        setReconnectTimer(null);
      }
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [isOpen, questionId, examId, isManuallyClosed]);

  const connectToChat = async (isReconnect = false) => {
    if (isManuallyClosed) return; // Prevent reconnection if manually closed

    if (!questionId) {
      setError('No question ID available');
      return;
    }

    if (!examId) {
      setError('No exam ID available');
      return;
    }

    setIsConnecting(true);
    setError('');
    if (!isReconnect) {
      setMessages([]);
      setReconnectStatus('');
    }

    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL;

      // Auth carried by the HttpOnly session cookie sent on the WebSocket upgrade.
      const wsUrl = apiUrl.replace('http://', 'ws://').replace('https://', 'wss://');
      const wsEndpoint = `${wsUrl}/api/question/${questionId}/chat?ExamId=${examId}`;

      // console.log('🔗 Connecting to AI Chat for question:', questionId);
      // console.log('🔗 Using exam ID:', examId);
      // console.log('🔐 WebSocket endpoint:', wsEndpoint);

      const ws = new WebSocket(wsEndpoint);

      ws.onopen = (event) => {
        // console.log('✅ AI Chat connected successfully');
        setIsConnected(true);
        setIsConnecting(false);
        setWebsocket(ws);
        websocketRef.current = ws;
        setReconnectAttempts(0);
        setReconnectStatus('');
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          setReconnectTimer(null);
        }
        // Remove reconnect message if present
        setMessages(prev => {
          if (reconnectMessageId) {
            return prev.filter(m => m.id !== reconnectMessageId).concat([{
              id: Date.now(),
              type: 'system',
              content: `Welcome to ExamFlip AI assistant`,
              timestamp: new Date()
            }]);
          }
          return prev.concat([{
            id: Date.now(),
            type: 'system',
            content: `Welcome to ExamFlip AI assistant`,
            timestamp: new Date()
          }]);
        });
        setReconnectMessageId(null);
      };

      ws.onmessage = (event) => {
        // console.log('📨 AI Response:', event.data);
        setIsAiTyping(false);
        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'bot',
          content: event.data,
          timestamp: new Date()
        }]);
      };

      ws.onclose = (event) => {
        // console.log('❌ AI Chat connection closed:', event.code, event.reason);
        setIsConnected(false);
        setWebsocket(null);
        setIsConnecting(false);
        websocketRef.current = null;

        if (!isManuallyClosed && event.code !== 1000) {
          attemptReconnect();
        }
      };

      ws.onerror = (error) => {
        console.error('❌ AI Chat error:', error);
        setIsConnecting(false);
        setIsConnected(false);
        if (reconnectAttempts >= 6) {
          setError('Failed to connect to AI assistant.');
        }
      };

    } catch (err) {
      console.error('❌ AI Chat connection error:', err);
      setError('Failed to establish connection: ' + err.message);
      setIsConnecting(false);
    }
  };

  // Reconnect logic
  const attemptReconnect = () => {
    setReconnectAttempts(prev => {
      const next = prev + 1;
      if (next <= 6) {
        setReconnectStatus(`Connection lost. Attempting to reconnect... (${next}/6)`);
      }
      if (next === 6) {
        setReconnectStatus('Failed to connect. Retry later...');
        setReconnectTimer(null);
        setReconnectMessageId(null);
        // Start auto-close timer
        if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = setTimeout(() => {
          handleDisconnect();
        }, 10000);
        return next;
      }
      if (next > 6) {
        // Do not retry anymore
        return next;
      }
      const timer = setTimeout(() => {
        connectToChat(true);
      }, 20000);
      setReconnectTimer(timer);
      return next;
    });
  };

  const sendMessage = () => {
    if (!inputMessage.trim() || !websocket || !isConnected || isAiTyping) {
      return;
    }

    const message = inputMessage.trim();
    
    // Add user message to chat
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    }]);

    try {
      websocket.send(message);
      setInputMessage('');
      setIsAiTyping(true);
    } catch (err) {
      console.error('❌ Error sending message:', err);
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'system',
        content: `Failed to send message: ${err.message}`,
        timestamp: new Date()
      }]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleDisconnect = () => {
    // console.log(`[QuestionAIChat] handleDisconnect called - current state:`, {
    //   isOpen,
    //   isMinimized,
    //   isConnected,
    //   websocketState: websocketRef.current ? websocketRef.current.readyState : 'null'
    // });

    if (websocketRef.current) {
      try {
        setIsManuallyClosed(true);
        const readyState = websocketRef.current.readyState;
        // console.log(`🔌 Closing WebSocket connection... Current readyState: ${readyState}`);
        
        if (readyState === 1) { // OPEN
          websocketRef.current.close(1000, 'User disconnected');
        } else {
          // console.log('WebSocket is not open, cannot close. Current readyState:', readyState);
        }
        
        websocketRef.current = null;
      } catch (err) {
        console.error('❌ Error closing WebSocket:', err);
      }
    }
    
    // Clean up timers and state
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      setReconnectTimer(null);
    }
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    
    // Reset connection state
    setIsConnected(false);
    setIsConnecting(false);
    setWebsocket(null);
    setReconnectAttempts(0);
    setReconnectMessageId(null);
    setError('');
    setReconnectStatus('');
    
    // Close the chat UI
    onClose();
  };

  const copyToClipboard = (htmlContent, messageId) => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = htmlContent;
    const plainText = tempElement.textContent || tempElement.innerText || '';
    navigator.clipboard.writeText(plainText).then(() => {
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 1200); // Hide after 1.2s
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // Drag functionality handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('.drag-handle')) {
      const modalElement = modalRef.current;
      if (modalElement) {
        const rect = modalElement.getBoundingClientRect();
        setIsDragging(true);
        setDragStart({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
        e.preventDefault();
      }
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      
      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const modalElement = modalRef.current;
      
      if (modalElement) {
        const modalRect = modalElement.getBoundingClientRect();
        const modalWidth = modalRect.width;
        const modalHeight = modalRect.height;
        
        // Constrain to viewport bounds with some padding
        const padding = 20;
        const constrainedX = Math.max(padding, Math.min(newX, viewportWidth - modalWidth - padding));
        const constrainedY = Math.max(padding, Math.min(newY, viewportHeight - modalHeight - padding));
        
        setModalPosition({ x: constrainedX, y: constrainedY });
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none'; // Prevent text selection while dragging
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, modalPosition, dragStart]);

  // Reset modal position when modal opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setModalPosition({ x: 0, y: 0 });
    }
  }, [isOpen, isMinimized]);

  if (!isOpen) return null;

  if (isMinimized) {
    return (
      <div className="ef-ai-mini" style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1000,
        background: isDarkMode ? '#1e293b' : '#ffffff',
        border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
        borderRadius: '24px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '8px 20px 8px 12px',
        cursor: 'pointer',
        minWidth: 0
      }}
        onClick={() => {
          // console.log(`[QuestionAIChat] 📱 Expanding chat from minimized state`);
          setIsMinimized(false);
        }}
        title="Expand AI Chat"
      >
  <img className="ef-ai-logo--avatar" src={ExamFlipLogo} alt="ExamFlip Logo" style={{ width: 28, height: 28, borderRadius: '50%' }} />
        <span style={{ color: isDarkMode ? '#f1f5f9' : '#1e293b', fontWeight: 600, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>
          AI Chat
        </span>
        <button
          onClick={e => { e.stopPropagation(); onClose(); }}
          className="ef-ai-icon-btn"
          style={{
            background: 'none',
            border: 'none',
            color: isDarkMode ? '#94a3b8' : '#64748b',
            cursor: 'pointer',
            fontSize: 18,
            marginLeft: 8,
            padding: 4
          }}
          title="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
    );
  }

  return (
    <div className="ef-ai-modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.1)',
      zIndex: 1000,
      padding: isMobile ? '10px' : '0'
    }}>
      <div 
        className="ef-ai-modal"
        ref={modalRef}
        style={{
          position: 'absolute',
          top: modalPosition.x === 0 && modalPosition.y === 0 ? '50%' : `${modalPosition.y}px`,
          left: modalPosition.x === 0 && modalPosition.y === 0 ? '50%' : `${modalPosition.x}px`,
          transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
          background: isDarkMode ? '#1e293b' : '#ffffff',
          borderRadius: isMobile ? '8px' : '12px',
          width: isMobile ? '95vw' : '100%',
          maxWidth: isMobile ? 'none' : '800px',
          height: isMobile ? '85vh' : '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
          cursor: isDragging ? 'grabbing' : 'default',
          transition: isDragging ? 'none' : 'all 0.2s ease'
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Header */}
        <div 
          className="drag-handle"
          style={{
            padding: isMobile ? '12px 16px' : '20px',
            borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '6px' : '8px',
            cursor: 'grab',
            userSelect: 'none'
          }}
        >
          <div className="ef-ai-header-top" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: isMobile ? '8px' : '12px', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', minWidth: 0 }}>
            <img 
              className="ef-ai-logo--header"
              src={ExamFlipLogo} 
              alt="ExamFlip Logo" 
              style={{ 
                width: isMobile ? '24px' : '32px', 
                height: isMobile ? '24px' : '32px', 
                borderRadius: '50%' 
              }}
            />
              <h2 style={{ 
                margin: 0, 
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '6px' : '8px',
                fontSize: isMobile ? '14px' : '18px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                ExamFlip AI Assistant
                <FontAwesomeIcon 
                  icon={faGripVertical} 
                  style={{
                    fontSize: '14px',
                    color: isDarkMode ? '#64748b' : '#94a3b8',
                    opacity: 0.7
                  }}
                  title="Drag to move"
                />
              </h2>
            </div>
            <div className="ef-ai-actions" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {/* Minimize Button */}
            <button
              onClick={() => {
                // console.log(`[QuestionAIChat] 📱 Minimizing chat`);
                setIsMinimized(true);
              }}
              className="ef-ai-icon-btn"
              style={{
                background: 'none',
                border: 'none',
                color: isDarkMode ? '#94a3b8' : '#64748b',
                cursor: 'pointer',
                fontSize: '20px',
                padding: '8px',
                marginRight: '2px'
              }}
              title="Minimize"
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="10" width="12" height="2" rx="1" fill="currentColor" />
              </svg>
            </button>
            {/* Close Button */}
            <button
              onClick={handleDisconnect}
              className="ef-ai-icon-btn"
              style={{
                background: 'none',
                border: 'none',
                color: isDarkMode ? '#94a3b8' : '#64748b',
                cursor: 'pointer',
                fontSize: '20px',
                padding: '8px'
              }}
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            </div>
          </div>
          <div className="ef-ai-status-row" style={{ 
            margin: 0, 
            fontSize: isMobile ? '12px' : '14px', 
            color: isDarkMode ? '#94a3b8' : '#64748b'
          }}>
            Status: {isConnected ? 'Connected' : 'Connecting...'}
          </div>
        </div>

        {/* Connection Status */}
        {isConnecting && (
          <div style={{
            padding: '16px 20px',
            background: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
            borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#3b82f6'
          }}>
            <FontAwesomeIcon icon={faSpinner} spin />
            <span>Connecting to AI assistant...</span>
          </div>
        )}

        {(reconnectStatus || (error && reconnectAttempts >= 6)) && (
          <div style={{
            padding: '16px 20px',
            background: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
            borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#ef4444',
            fontWeight: 500
          }}>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>
              {reconnectStatus ? reconnectStatus : error}
            </span>
          </div>
        )}

        {/* Messages Area */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {messages.length === 0 && isConnected ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              color: isDarkMode ? '#94a3b8' : '#64748b',
              padding: isMobile ? '20px 16px' : '20px'
            }}>
              {/* Logo - hide on mobile */}
              {!isMobile && (
                <img 
                  className="ef-ai-logo--welcome"
                  src={ExamFlipLogo} 
                  alt="ExamFlip Logo" 
                  style={{ 
                    width: '48px', 
                    height: '48px', 
                    marginBottom: '16px', 
                    opacity: 0.5
                  }}
                />
              )}
              {/* Debug: Log mobile state */}
              {/* {console.log('[QuestionAIChat] Rendering welcome area:', { 
                isMobile, 
                showLogo: !isMobile,
                width: window.innerWidth 
              }) || null} */}
              <p style={{ 
                fontSize: isMobile ? '16px' : '16px',
                margin: isMobile ? '0 0 12px 0' : '0 0 8px 0',
                fontWeight: isMobile ? '600' : 'normal'
              }}>
                Welcome to the ExamFlip AI Assistant!
              </p>
              <p style={{ 
                fontSize: isMobile ? '14px' : '14px', 
                opacity: 0.8,
                margin: 0,
                padding: isMobile ? '0 12px' : '0 16px',
                lineHeight: '1.5'
              }}>
                Type your question to get started. I'm here to help with your practice test.
              </p>
            </div>
          ) : messages.length === 0 && !isConnected ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              color: isDarkMode ? '#94a3b8' : '#64748b',
              padding: isMobile ? '20px 16px' : '20px'
            }}>
              <p style={{ 
                fontSize: isMobile ? '16px' : '16px',
                margin: isMobile ? '0 0 12px 0' : '0 0 8px 0',
                fontWeight: isMobile ? '600' : 'normal'
              }}>
                Connecting to AI Assistant...
              </p>
              <p style={{ 
                fontSize: isMobile ? '14px' : '14px', 
                opacity: 0.8,
                margin: 0,
                padding: isMobile ? '0 12px' : '0 16px',
                lineHeight: '1.5'
              }}>
                Please wait while we establish the connection.
              </p>
            </div>
          ) : (
            messages.map((message) => (
              <div key={message.id} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                flexDirection: message.type === 'user' ? 'row-reverse' : 'row'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: message.type === 'user' ? '#3b82f6' : 
                             message.type === 'bot' ? '#10b981' : '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0
                }}>
                  {message.type === 'user' ? (
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      {getUserInitials()}
                    </span>
                  ) : (
                    <img 
                      className="ef-ai-logo--avatar"
                      src={ExamFlipLogo} 
                      alt="ExamFlip Logo" 
                      style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                    />
                  )}
                </div>
                <div style={{
                  maxWidth: '70%',
                  background: message.type === 'user' ? '#3b82f6' :
                             message.type === 'system' ? (isDarkMode ? 'rgba(100, 116, 139, 0.2)' : 'rgba(100, 116, 139, 0.1)') :
                             (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc'),
                  color: message.type === 'user' ? 'white' : (isDarkMode ? '#f1f5f9' : '#1e293b'),
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: message.type !== 'user' ? (isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0') : 'none',
                  position: 'relative'
                }}>
                  <div 
                    className={message.type !== 'user' ? 'ai-chat-content' : ''}
                    dangerouslySetInnerHTML={{ 
                      __html: message.content.replace(/\n/g, '<br>') 
                    }} 
                  />
                  <div style={{ 
                    fontSize: '12px', 
                    opacity: 0.7, 
                    marginTop: '4px',
                    color: message.type === 'user' ? 'rgba(255,255,255,0.8)' : (isDarkMode ? '#94a3b8' : '#64748b')
                  }}>
                    {formatTime(message.timestamp)}
                  </div>
                  {message.type === 'bot' && (
                    <div style={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button
                        onClick={() => copyToClipboard(message.content, message.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: isDarkMode ? '#94a3b8' : '#64748b',
                          cursor: 'pointer',
                          fontSize: '12px',
                          opacity: 0.7,
                          padding: '4px',
                              marginLeft: '8px',   // Add left margin
    marginBottom: '6px', // Add bottom margin
    position: 'relative',
    zIndex: 2
                        }}
                        title="Copy message"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                      {copiedMessageId === message.id && (
                        <span style={{
                          background: isDarkMode ? '#334155' : '#e0e7ef',
                          color: isDarkMode ? '#f1f5f9' : '#1e293b',
                          borderRadius: '6px',
                          padding: '2px 8px',
                          fontSize: '12px',
                          fontWeight: 500,
                          opacity: 0.9,
                          marginLeft: '2px',
                          transition: 'opacity 0.2s'
                        }}>
                          Copied!
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}

          {/* AI Typing Indicator */}
          {isAiTyping && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: '#10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                flexShrink: 0
              }}>
                <img 
                  className="ef-ai-logo--avatar"
                  src={ExamFlipLogo} 
                  alt="ExamFlip Logo" 
                  style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                />
              </div>
              <div style={{
                background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
                padding: '12px 16px',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: '#10b981',
                    animation: 'bounce 1.4s infinite ease-in-out'
                  }}></div>
                  <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: '#10b981',
                    animation: 'bounce 1.4s infinite ease-in-out -0.32s'
                  }}></div>
                  <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: '#10b981',
                    animation: 'bounce 1.4s infinite ease-in-out -0.16s'
                  }}></div>
                </div>
                <span style={{ fontSize: '14px', opacity: 0.8 }}>ExamFlip AI Assistant is typing...</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div style={{
          padding: '20px',
          borderTop: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '12px'
          }}>
            <div style={{ flex: 1 }}>
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isConnected ? "Type your message..." : "Connecting..."}
                disabled={!isConnected}
                style={{
                  width: '100%',
                  minHeight: '44px',
                  maxHeight: '120px',
                  padding: '12px 16px',
                  border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  fontSize: '14px',
                  resize: 'none',
                  outline: 'none',
                  fontFamily: 'inherit'
                }}
              />
            </div>
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || !isConnected || isAiTyping}
              style={{
                padding: '12px 16px',
                background: !inputMessage.trim() || !isConnected || isAiTyping ? 
                  (isDarkMode ? 'rgba(100, 116, 139, 0.3)' : '#e5e7eb') : '#3b82f6',
                color: !inputMessage.trim() || !isConnected || isAiTyping ? 
                  (isDarkMode ? 'rgba(241, 245, 249, 0.5)' : '#9ca3af') : 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: !inputMessage.trim() || !isConnected || isAiTyping ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                minWidth: '60px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
          
          {/* Close Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '16px'
          }}>
            <button
              onClick={handleDisconnect}
              className="ef-ai-bottom-close-btn"
              style={{
                padding: '10px 24px',
                background: isDarkMode ? 'rgba(220, 53, 69, 0.15)' : 'rgba(220, 53, 69, 0.08)',
                color: isDarkMode ? '#b9aeaeff' : '#99212dff',
                border: isDarkMode ? '1px solid rgba(220, 53, 69, 0.3)' : '1px solid rgba(220, 53, 69, 0.2)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={isDarkMode ? (e) => {
                e.target.style.background = 'rgba(220, 53, 69, 0.25)';
                e.target.style.borderColor = 'rgba(220, 53, 69, 0.4)';
              } : undefined}
              onMouseLeave={isDarkMode ? (e) => {
                e.target.style.background = 'rgba(220, 53, 69, 0.15)';
                e.target.style.borderColor = 'rgba(220, 53, 69, 0.3)';
              } : undefined}
              title="Close AI Chat"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes bounce {
            0%, 80%, 100% {
              transform: scale(0);
            }
            40% {
              transform: scale(1);
            }
          }
          .ai-chat-content {
            line-height: 1.5;
          }
          .ai-chat-content p {
            margin: 0 0 8px 0;
          }
          .ai-chat-content ul, .ai-chat-content ol {
            margin: 8px 0;
            padding-left: 20px;
          }
          .ai-chat-content li {
            margin: 4px 0;
          }
          .ai-chat-content code {
            background: ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
            padding: 2px 4px;
            border-radius: 3px;
            font-size: 0.9em;
          }
          .ai-chat-content pre {
            background: ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
            padding: 12px;
            border-radius: 6px;
            overflow-x: auto;
            margin: 8px 0;
          }
        `}
      </style>
    </div>
  );
});

export default QuestionAIChat;
